import React, { useEffect } from "react";
import { TextField } from "@telia/teddy";
import { FormConsumer } from "../../molecules/Form/FormContext";

let lastFocusedInput: HTMLInputElement | null = null;

if (typeof window !== "undefined") {
    window.addEventListener("focus", () => {
        if (lastFocusedInput?.isConnected) {
            try {
                lastFocusedInput.focus();
            } catch (err) {
                if (err instanceof DOMException && err.name === "InvalidStateError") {
                    lastFocusedInput = null;
                    return;
                }
                throw err;
            }
        } else {
            lastFocusedInput = null;
        }
    });
}

interface InputInitializerProps {
    children: React.ReactNode;
    inputIdentifier: string;
    inputValue: string;
    onInitialize: (
        inputName: string,
        inputValue: string,
        previousValue: string,
        shouldValidate: boolean,
        visible: boolean,
    ) => void;
    onUnmount: (id: string) => void;
}

const InputInitializer: React.FC<InputInitializerProps> = ({
    inputValue,
    inputIdentifier,
    children,
    onInitialize,
    onUnmount,
}) => {
    useEffect(() => {
        onInitialize(inputIdentifier, inputValue, inputValue, false, true);

        return () => {
            onUnmount(inputIdentifier);
        };
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, []);

    return <>{children}</>;
};

interface InputProps {
    className?: string;
    disabled?: boolean;
    helpText?: React.ReactNode;
    id?: string;
    inputRef?: React.Ref<HTMLInputElement>;
    label?: string;
    name?: string;
    placeholder?: string;
    type?: string;
    [key: string]: unknown;
}

const NON_SELECTABLE_TYPES = new Set(["email"]);

const Input: React.FC<InputProps> = ({
    disabled,
    label,
    name = "",
    helpText = "",
    type = "text",
    className,
    inputRef,
    ...rest
}) => {
    const inputMode = type === "email" ? "email" : undefined;
    const resolvedType = NON_SELECTABLE_TYPES.has(type) ? "text" : type;

    return (
        <FormConsumer>
            {({ removeValue, setValue, inputPrefix = "", errors = {}, values = {}, started = {} }) => {
                const inputIdentifier = `${inputPrefix} ${name}`.trim().replace(/\s/g, "-");
                const isValid = started[name] && !errors[name];
                return (
                    <InputInitializer
                        inputValue={values[name] || ""}
                        inputIdentifier={inputIdentifier}
                        onInitialize={setValue}
                        onUnmount={removeValue}
                    >
                        <TextField
                            disabled={disabled}
                            errors={errors[name] ? [errors[name]] : undefined}
                            isValid={isValid}
                            className={className}
                        >
                            {label && <TextField.Label>{label}</TextField.Label>}
                            <TextField.Input
                                ref={inputRef}
                                name={inputIdentifier}
                                onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                                    lastFocusedInput = event.target as HTMLInputElement;
                                }}
                                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                                    if (document.hasFocus()) {
                                        lastFocusedInput = null;
                                    }
                                    const trimmedValue = (event.target.value || "").trim();
                                    setValue(inputIdentifier, trimmedValue, values[name] || "", true, true);
                                }}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    setValue(inputIdentifier, event.target.value, values[name] || "", false, true)
                                }
                                type={resolvedType}
                                inputMode={inputMode}
                                value={values[name] || ""}
                                data-di-mask
                                {...rest}
                            />
                            {!errors[name] && helpText && <TextField.HelperText>{helpText}</TextField.HelperText>}
                            <TextField.ErrorList />
                        </TextField>
                    </InputInitializer>
                );
            }}
        </FormConsumer>
    );
};

export default Input;
